/**
 * Start-recording lifecycle FSM.
 *
 * Owns `handleStart`: ensures the mic monitoring chain is alive, resumes the
 * AudioContext, validates the stream + mime type + session, opens the
 * streaming WebSocket (when in streaming mode), spins up MediaRecorder
 * with the chunk pipeline and the speech heartbeat, and finally transitions
 * wrapper state to "recording".
 *
 * The first chunk carries the WebM header and is cached here so pause/resume
 * can re-send it. Every chunk is streamed; nothing is gated on volume (the
 * voice-activity gate was removed 2026-08-12, see docs/adr/0036). Speech is
 * signalled separately with a `vad: speaking` control message.
 */
import type { TStreamControlMessage } from "@lexicanon/shared"
import type { I18n } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import { useCallback, useRef } from "react"
import { RECORDER_AUDIO_BITS_PER_SECOND } from "../../../hooks/useAudioPipeline"
import { checkStorageQuota } from "../../../hooks/useIndexedDB"
import type { TRecordingMode, TRecordingState } from "../interfaces"

const VAD_SILENCE_THRESHOLD = 0.04

interface IProps {
  meetingId: string
  mode: TRecordingMode
  monitoring: boolean
  selectedDevice: string | null
  audioCtxRef: React.MutableRefObject<AudioContext | null>
  streamRef: React.MutableRefObject<MediaStream | null>
  destinationRef: React.MutableRefObject<MediaStreamAudioDestinationNode | null>
  currentVolumeRef: React.MutableRefObject<number>
  startTimeRef: React.MutableRefObject<number>
  pausedElapsedRef: React.MutableRefObject<number>
  mediaRecorderRef: React.MutableRefObject<MediaRecorder | null>
  wsRef: React.MutableRefObject<WebSocket | null>
  chunksRef: React.MutableRefObject<Blob[]>
  activeMimeTypeRef: React.MutableRefObject<string>
  /** Caches the first MediaRecorder chunk (the WebM/Opus init header) so the
   *  header can be re-sent when pause/resume opens a fresh WebSocket. */
  firstHeaderChunkRef: React.MutableRefObject<Blob | null>
  lastUploadedChunkIndexRef: React.MutableRefObject<number>
  uploadBatchIndexRef: React.MutableRefObject<number>
  lastSegmentAtRef: React.MutableRefObject<number>
  lastAudioAtRef: React.MutableRefObject<number>
  startMonitoring: (deviceId: string) => Promise<void>
  getMimeType: () => string | null
  requestWakeLock: () => Promise<void>
  startElapsedTimer: () => void
  startPeriodicSave: () => void
  startChunkUploadTimer: () => void
  setIdbWarning: (warning: boolean) => void
  resetWsState: () => void
  openStreamingWs: () => WebSocket | null
  setState: (state: TRecordingState) => void
  setRecordingActive: (active: boolean) => void
  setRecovered: (saved: null) => void
  setError: (err: string) => void
  sessionHealthStatus: "ok" | "expired" | "error" | "unknown"
  i18n: I18n
}

interface IUseStartRecordingResult {
  handleStart: () => Promise<void>
}

export function useStartRecording(props: IProps): IUseStartRecordingResult {
  const {
    meetingId,
    mode,
    monitoring,
    selectedDevice,
    audioCtxRef,
    streamRef,
    destinationRef,
    currentVolumeRef,
    startTimeRef,
    pausedElapsedRef,
    mediaRecorderRef,
    wsRef,
    chunksRef,
    activeMimeTypeRef,
    firstHeaderChunkRef,
    lastUploadedChunkIndexRef,
    uploadBatchIndexRef,
    lastSegmentAtRef,
    lastAudioAtRef,
    startMonitoring,
    getMimeType,
    requestWakeLock,
    startElapsedTimer,
    startPeriodicSave,
    startChunkUploadTimer,
    setIdbWarning,
    resetWsState,
    openStreamingWs,
    setState,
    setRecordingActive,
    setRecovered,
    setError,
    sessionHealthStatus,
    i18n,
  } = props

  // Re-entry guard — handleStart awaits startMonitoring + audioCtx.resume before
  // flipping state to "recording", so a fast double-click would otherwise spin up
  // a SECOND MediaRecorder on the same stream. Bail if a start is already in
  // flight. See §1.5.
  const pendingRef = useRef(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: many deps are stable refs
  const runStart = useCallback(async (): Promise<void> => {
    if (!monitoring && selectedDevice) {
      await startMonitoring(selectedDevice)
    }
    if (audioCtxRef.current?.state === "suspended") {
      await audioCtxRef.current.resume()
    }
    const stream = streamRef.current
    if (!stream) {
      setError(i18n._(msg`No microphone stream available`))
      return
    }
    const mimeType = getMimeType()
    if (!mimeType) {
      setError(i18n._(msg`Your browser does not support audio recording`))
      return
    }
    activeMimeTypeRef.current = mimeType

    if (sessionHealthStatus === "expired") {
      setError(i18n._(msg`Your session has expired. Please refresh the page and sign in again before recording.`))
      return
    }

    const quota = await checkStorageQuota()
    if (!quota.ok) {
      setIdbWarning(true)
    }

    lastUploadedChunkIndexRef.current = 0
    uploadBatchIndexRef.current = 0

    resetWsState()

    await requestWakeLock()

    if (mode === "streaming") {
      openStreamingWs()
    }

    const recordingStream = destinationRef.current?.stream ?? stream
    const recorder = new MediaRecorder(recordingStream, {
      mimeType,
      audioBitsPerSecond: RECORDER_AUDIO_BITS_PER_SECOND,
    })
    chunksRef.current = []
    firstHeaderChunkRef.current = null
    recorder.ondataavailable = (e): void => {
      lastAudioAtRef.current = Date.now()
      if (e.data.size > 0) {
        chunksRef.current.push(e.data)
        // The very first chunk carries the WebM/Opus init header. Cache it so
        // pause/resume (new WS, same recorder) can re-send the header.
        if (firstHeaderChunkRef.current === null) {
          firstHeaderChunkRef.current = e.data
        }
        if (mode === "streaming" && wsRef.current?.readyState === WebSocket.OPEN) {
          // Stream EVERY chunk — no VAD gate. Gating the WS send starved the live
          // transcript (the analyser volume reads below threshold for normal
          // speech, especially on Safari) AND created timeline gaps that stalled
          // the server-side ffmpeg transcoder on resume. The durable REST-upload
          // path already sends every chunk and streaming is metered by wall-clock,
          // so streaming continuously costs nothing extra.
          e.data.arrayBuffer().then((buf) => {
            wsRef.current?.send(buf)
          })
          // Speech heartbeat: with all chunks flowing, the server can no longer
          // infer speech from "a chunk arrived", so tell it when we're actually
          // speaking. Only "speaking" is sent — silence stays quiet so the room
          // stale-adapter check goes dormant during genuine quiet.
          if (currentVolumeRef.current >= VAD_SILENCE_THRESHOLD) {
            const vadMsg: TStreamControlMessage = { type: "vad", speaking: true }
            wsRef.current?.send(JSON.stringify(vadMsg))
          }
        }
      }
    }
    recorder.onerror = (): void => {}
    recorder.start(2000)
    mediaRecorderRef.current = recorder
    startTimeRef.current = Date.now()
    pausedElapsedRef.current = 0
    lastSegmentAtRef.current = Date.now()
    lastAudioAtRef.current = Date.now()
    setState("recording")
    setRecordingActive(true)
    sessionStorage.setItem("lexicanon:activeRecording", meetingId)
    setRecovered(null)
    startElapsedTimer()
    startPeriodicSave()
    startChunkUploadTimer()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- many deps are stable refs (see biome-ignore on declaration)
  }, [
    monitoring,
    selectedDevice,
    startMonitoring,
    startPeriodicSave,
    startElapsedTimer,
    startChunkUploadTimer,
    meetingId,
    mode,
    openStreamingWs,
    resetWsState,
    getMimeType,
    requestWakeLock,
    sessionHealthStatus,
    setRecordingActive,
  ])

  const handleStart = useCallback(async (): Promise<void> => {
    if (pendingRef.current) return
    pendingRef.current = true
    try {
      await runStart()
    } finally {
      pendingRef.current = false
    }
  }, [runStart])

  return { handleStart }
}
